import { useClientRole } from "@/components/client-role";
import { PageHeader } from "@/components/dashboard/page-header";
import { RolesHome } from "@/components/dashboard/roles-home";
import { ErrorState, Skeleton } from "@/components/dashboard/state";
import { clientApi } from "@/lib/client-api";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowDownRight, ArrowUpRight, Minus, Plus } from "lucide-react";

type Trend = "up" | "down" | "flat";

/** A word-sized trend behind each headline number — a soft NEUTRAL area (filled mass, not a 1px stroke)
 *  with a single brand-yellow "now" dot marking where it stands. The filled area reads at this size and
 *  in both themes; the thin-yellow-line version was near-invisible on the light canvas. `gid` scopes the
 *  gradient per card. Series are representative for this static iteration. */
function Sparkline({ data, gid }: { data: number[]; gid: string }) {
  const w = 84;
  const h = 30;
  const p = 3;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const at = (v: number, i: number): readonly [number, number] => {
    const x = p + (i / (data.length - 1)) * (w - 2 * p);
    const y = h - p - ((v - min) / span) * (h - 2 * p);
    return [x, y] as const;
  };
  const line = data.map((v, i) => at(v, i).join(",")).join(" ");
  const [lastX, lastY] = at(data[data.length - 1] ?? 0, data.length - 1);
  const area = `${p},${h - p} ${line} ${w - p},${h - p}`;
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="shrink-0 overflow-visible text-foreground"
      role="img"
      aria-label="trend"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gid})`} />
      <polyline
        points={line}
        fill="none"
        className="text-muted-foreground"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill="var(--brand-accent)" stroke="var(--color-card)" strokeWidth="1" />
    </svg>
  );
}

/** The label → delta chip: direction + magnitude vs. the prior period, colour-coded. Representative. */
function DeltaChip({ trend, label }: { trend: Trend; label: string }) {
  const Icon = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : Minus;
  const tone =
    trend === "up"
      ? "text-[var(--color-success)]"
      : trend === "down"
        ? "text-[var(--color-warning)]"
        : "text-muted-foreground";
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums", tone)}>
      <Icon className="size-3" />
      {label}
    </span>
  );
}

/** One portfolio metric: label + delta over a big number sitting beside its trend line, with a one-line
 *  "so what" underneath. Reads current state, direction, and magnitude at a glance (per the review that
 *  the flat number strip felt inert). Kept secondary to the roles table below. */
function Kpi({
  label,
  value,
  trend,
  delta,
  note,
  spark,
}: {
  label: string;
  value: string | number;
  trend: Trend;
  delta: string;
  note: string;
  spark: number[];
}) {
  return (
    <div className="flex flex-col gap-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="eyebrow whitespace-nowrap text-[10px]">{label}</span>
        <DeltaChip trend={trend} label={delta} />
      </div>
      <div className="flex items-end justify-between gap-3">
        <span className="text-3xl font-semibold leading-none tabular-nums text-foreground">{value}</span>
        <Sparkline data={spark} gid={`spark-${label.replace(/\s+/g, "")}`} />
      </div>
      <span className="text-[11px] leading-tight text-muted-foreground">{note}</span>
    </div>
  );
}

/** The client home: a portfolio metrics strip over ONE roles table (tiers are a filter, not navigation),
 *  with a table/org-chart toggle. Built for a client observing recruitment progress at a glance. */
export function OverviewPage() {
  const role = useClientRole();
  const { data, isError, refetch } = useQuery({
    queryKey: ["client", "overview"],
    queryFn: () => clientApi.overview(),
  });
  const kpis = data?.kpis;
  const tiers = data?.tiers;
  const loading = !data && !isError;
  const nearClosureTotal = tiers
    ? Object.values(tiers).reduce((s, rows) => s + rows.reduce((a, r) => a + r.nearClosure, 0), 0)
    : 0;

  // Representative week-over-week trends for the static iteration — the shapes end at the live figure
  // above so the strip stays internally consistent while conveying movement (devs wire real series later).
  const cards = [
    {
      label: "Active roles",
      value: kpis?.activeSearches ?? 0,
      trend: "up" as const,
      delta: "+2",
      note: "2 opened this week",
      spark: [11, 12, 12, 13, 13, 14, 14],
    },
    {
      label: "Candidates",
      value: kpis?.inPipeline ?? 0,
      trend: "up" as const,
      delta: "+6",
      note: "6 added across the pipeline",
      spark: [24, 27, 29, 31, 33, 35, 37],
    },
    {
      label: "Near closure",
      value: nearClosureTotal,
      trend: "up" as const,
      delta: "+1",
      note: "1 more entered final stages",
      spark: [2, 3, 3, 4, 4, 4, 5],
    },
    {
      label: "Offers out",
      value: kpis?.atOffer ?? 0,
      trend: "flat" as const,
      delta: "0",
      note: "2 awaiting candidate decision",
      spark: [1, 1, 2, 2, 2, 2, 2],
    },
  ];

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader title="Recruitment overview" description="Every open role and how far along it is — at a glance.">
        {role === "admin" ? (
          <Link
            to={"/admin-view/roles/new" as never}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-foreground px-3 text-sm font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="size-4" /> New role
          </Link>
        ) : null}
      </PageHeader>

      {isError ? (
        <ErrorState onRetry={() => refetch()}>Couldn't load the overview.</ErrorState>
      ) : loading ? (
        // Skeleton mirrors the real layout (metrics strip + roles table) so nothing shifts on load.
        <>
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-9 w-80 rounded-md" />
          <Skeleton className="h-72 w-full rounded-xl" />
        </>
      ) : (
        <>
          {/* Portfolio metrics — four headline numbers with movement, deliberately secondary to the table. */}
          <div className="grid grid-cols-2 divide-x divide-y overflow-hidden rounded-xl border bg-card lg:grid-cols-4 lg:divide-y-0">
            {cards.map((c) => (
              <Kpi key={c.label} {...c} />
            ))}
          </div>

          {tiers ? <RolesHome tiers={tiers} /> : null}
        </>
      )}
    </div>
  );
}
